/**
 * CellPopover — generic floating panel anchored to a trigger element.
 *
 * Hosts a complex cell editor (e.g. a multi-field form, a search picker) above
 * a table cell without being clipped by the table's `overflow`/stacking
 * context. Positioning is ported from `SearchColumnsPopover`
 * (`apps/admin/src/components/modules/SearchColumnsPopover.tsx`) — portal to
 * `document.body`, position computed from the anchor's
 * `getBoundingClientRect()` in a `useLayoutEffect`, recomputed on window
 * resize/scroll — generalized here to accept arbitrary `children` instead of
 * the hardcoded checkbox list, so any cell-editor UI can reuse the same
 * anchoring mechanics.
 *
 * Escape: listener jest globalny (`document`) w FAZIE PRZECHWYTYWANIA i woła `preventDefault`,
 * więc okno nakładkowe pod popoverem (`Overlay`) nie zamyka się razem z nim. Skutek uboczny:
 * listener widzi Escape PRZED elementami w `children`, więc nakładka zagnieżdżona w `children`
 * (lista, kalendarz) nie zamknie „tylko swojego poziomu" — jeden Escape zamknie popover razem
 * z nią. Tak jest dziś w `NumberingCell` (Radix `Dropdown` w popoverze) i było też przed
 * przestawieniem na fazę przechwytywania (listener nie sprawdzał `defaultPrevented`); poprawka
 * wymagałaby strażnika „nie zamykaj, gdy otwarta jest warstwa Radix" (otwarty dług).
 * UWAGA: `SearchableSelect`
 * w `children` wymagałby najpierw podniesienia `zIndex` jego panelu (60) ponad 9999 — dziś
 * renderowałby się POD overlayem popovera (`z-[9998]`/`z-[9999]`) i był niedostępny dla kliknięć.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { computeAnchoredPanelPosition } from '@/lib/anchored-panel';

export interface CellPopoverProps {
  open: boolean;
  /**
   * The cell/trigger element the panel anchors under. Utwórz go przez
   * `useRef<HTMLElement>(null)` — węższy typ elementu (np. `useRef<HTMLTableCellElement>`)
   * wymaga rzutowania przy przekazaniu (inwariancja `RefObject` w typach React 19).
   */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
  /** Extra CSS class applied to the floating panel. */
  className?: string;
  /** Dostępna etykieta panelu (panel ma `role="dialog"`). */
  ariaLabel?: string;
  'data-testid'?: string;
}

export function CellPopover({
  open,
  anchorRef,
  onClose,
  children,
  className,
  ariaLabel,
  'data-testid': testId,
}: CellPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Pozycja liczona z prostokąta komórki ORAZ z realnego rozmiaru panelu (wspólny helper z listami
  // rozwijanymi): gdy pod komórką brakuje miejsca na cały panel, idzie NAD nią; wysokość docięta do
  // okna (w środku scroll), lewa krawędź przesunięta, by panel nie wyjechał za prawy brzeg. Dawniej
  // panel stał zawsze pod komórką — przy niższych wierszach przyciski „Zatwierdź/Anuluj” lądowały
  // poza ekranem i zmian nie dało się zapisać.
  const [pos, setPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
  } | null>(null);

  const recalc = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const naturalHeight = panel.scrollHeight;
    const p = computeAnchoredPanelPosition(anchor.getBoundingClientRect(), {
      minWidth: panel.offsetWidth,
      preferredMaxHeight: naturalHeight,
      minUsableHeight: naturalHeight,
    });
    setPos(
      p.placement === 'above' && p.anchorBottom !== undefined
        ? { bottom: p.anchorBottom, left: p.left, maxHeight: p.maxHeight }
        : { top: p.top, left: p.left, maxHeight: p.maxHeight },
    );
  }, [anchorRef]);

  // Pierwszy pomiar po zamontowaniu panelu (renderowany niewidoczny, dopóki nie znamy pozycji).
  useLayoutEffect(() => {
    if (open) recalc();
    else setPos(null);
  }, [open, recalc]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [open, recalc]);

  // Esc zamyka popover tylko, gdy jest otwarty. Faza przechwytywania + `preventDefault`: okno
  // nakładkowe (`Overlay`), w którym stoi popover, widzi wtedy Esc jako już obsłużony i nie zamyka
  // się razem z nim (ani nie pyta o porzucenie zmian).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        role="presentation"
        className="fixed inset-0 z-[9998]"
        onClick={onClose}
        aria-hidden="true"
        data-testid={testId ? `${testId}-overlay` : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={ariaLabel}
        className={[
          'fixed z-[9999] overflow-y-auto rounded-md border border-border-primary bg-bg-primary shadow-lg',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          pos
            ? {
                top: pos.top,
                bottom: pos.bottom,
                left: pos.left,
                maxHeight: pos.maxHeight || undefined,
              }
            : { top: 0, left: 0, visibility: 'hidden' }
        }
        data-testid={testId}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
